import { useEffect, useState } from "react";
import { Box, Grid } from "@mui/material";
import {
  GetAggregateScoresRefreshInfoDocument,
  UiAction,
  UiPage,
  useGetAggregateScoresRefreshInfoQuery,
  useLoadOrganizationStatsQuery,
  useRefreshAggregateScoresMutation,
  useRegisterUserActionMutation,
} from "~/operations";
import { Metrics } from "~/lib/arrow";
import { Org } from "~/lib/types";
import {
  Build as BuildIcon,
  QueryStats as QueryStatsIcon,
  WarningAmber as WarningAmberIcon,
} from "@mui/icons-material";
import { TopVulnerabilitiesFound } from "./top-vulnerabilities-found";
import { AssetsToday } from "./assets-today";
import { TopVulnerabilities } from "./top-vulnerabilities";
import { SpacesGlance } from "./spaces-glance";
import { EndOfLife } from "./end-of-life";
import { LatestFixes } from "./latest-fixes";
import { OrgBoomStats } from "./org-boom-stats";
import { DashboardCard } from "./dashboard-card";
import { HomeDashboardHeader } from "~/pages/space/home-dashboard/components/HomeDashboardHeader";
import { setDocumentTitle } from "~/lib/commonUtils";
import { ExplorerIcon, SpacesIcon, VulnerabilitiesIcon } from "~/icons";
import { ScopeType } from "~/hooks/useScope";
import type { SpaceEdge } from "./types";

interface NewOrganizationDashboardPageProps {
  org: Org;
}

export function NewOrganizationDashboardPage({
  org,
}: NewOrganizationDashboardPageProps) {
  const [registerUserAction] = useRegisterUserActionMutation();
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Send user action mutation on mount
  useEffect(() => {
    registerUserAction({
      variables: {
        input: {
          action: UiAction.View,
          page: UiPage.OrgDashboard,
          scopeMrn: org.mrn,
        },
      },
    }).then();
  }, []);

  const [refreshAggregateScores] = useRefreshAggregateScoresMutation({
    refetchQueries: [GetAggregateScoresRefreshInfoDocument],
  });

  const {
    data: refreshInfoData,
    refetch: refetchRefreshInfo,
    startPolling,
    stopPolling,
  } = useGetAggregateScoresRefreshInfoQuery({
    variables: {
      groupMrn: org.mrn,
    },
  });

  useEffect(() => {
    if (refreshInfoData?.aggregateScoresRefreshInfo.refreshInProgress) {
      startPolling(10000);
    } else {
      stopPolling();
    }
  }, [refreshInfoData?.aggregateScoresRefreshInfo.refreshInProgress]);

  const lastUpdatedTime =
    refreshInfoData?.aggregateScoresRefreshInfo.lastUpdatedAt || "";

  const onRefreshValues = () => {
    setIsRefreshing(true);

    refreshAggregateScores({
      variables: { groupMrn: org.mrn },
    }).then(async () => {
      await refetchRefreshInfo();
      setIsRefreshing(false);
    });
  };

  const orgStatsResult = useLoadOrganizationStatsQuery({
    variables: {
      mrn: org.mrn,
      first: 10,
      metricMrns: [Metrics.AssetsRiskRatingDistribution],
    },
  });

  const spaceEdges: SpaceEdge[] =
    orgStatsResult.data?.organization?.spacesList?.edges || [];
  const totalSpaces =
    orgStatsResult.data?.organization?.spacesList?.totalCount ?? 0;

  setDocumentTitle(org.name);

  return (
    <Box>
      {/* Heading */}
      <HomeDashboardHeader
        context={ScopeType.Organization}
        title={org.name}
        description={org.description}
        onRefresh={onRefreshValues}
        lastRefreshed={lastUpdatedTime}
        isRefreshing={isRefreshing}
      />
      <Grid container spacing={3}>
        {/* Row 1: Vulnerability Stats + Assets Today */}
        <Grid size={{ lg: 8, md: 6, sm: 12 }}>
          <DashboardCard
            title="Vulnerability Stats"
            subtitle="Breakdown Of Findings Across The Organization"
            icon={<QueryStatsIcon />}
          >
            <OrgBoomStats
              organizationMrn={org.mrn}
              spaceEdges={spaceEdges}
              spacesLoading={orgStatsResult.loading}
              totalSpaces={totalSpaces}
            />
          </DashboardCard>
        </Grid>
        <Grid size={{ lg: 4, md: 6, sm: 12 }}>
          <DashboardCard
            title="Assets Today"
            icon={<ExplorerIcon />}
            sx={{ height: "100%" }}
          >
            <AssetsToday organizationMrn={org.mrn} />
          </DashboardCard>
        </Grid>

        {/* Row 2: Top New Vulnerabilities Found + Top Vulnerabilities */}
        <Grid size={{ lg: 8, md: 6, sm: 12 }}>
          <DashboardCard
            title="Top New Vulnerabilities Found"
            subtitle="Critical & High CVSS Vulnerabilities Over The Last 4 Weeks"
            icon={<VulnerabilitiesIcon />}
            sx={{ height: 256 }}
          >
            <TopVulnerabilitiesFound organizationMrn={org.mrn} />
          </DashboardCard>
        </Grid>
        <Grid size={{ lg: 4, md: 6, sm: 12 }}>
          <DashboardCard
            title="Top Vulnerabilities"
            icon={<VulnerabilitiesIcon />}
            sx={{ height: 256 }}
          >
            <TopVulnerabilities organizationMrn={org.mrn} />
          </DashboardCard>
        </Grid>

        {/* Row 3: Latest Fixes + Spaces At A Glance & End Of Life */}
        <Grid size={{ lg: 8, md: 6, sm: 12 }}>
          <DashboardCard
            title="Latest Fixes"
            subtitle="Recently Remediated Vulnerabilities"
            icon={<BuildIcon />}
            sx={{ height: "100%" }}
          >
            <LatestFixes
              spaceEdges={spaceEdges}
              spacesLoading={orgStatsResult.loading}
            />
          </DashboardCard>
        </Grid>
        <Grid container spacing={3} size={{ lg: 4, md: 6, sm: 12 }}>
          <Grid size={12}>
            <DashboardCard
              title="Spaces At A Glance"
              icon={<SpacesIcon />}
              sx={{ height: 248 }}
            >
              <SpacesGlance
                spaceEdges={spaceEdges}
                spacesLoading={orgStatsResult.loading}
              />
            </DashboardCard>
          </Grid>
          <Grid size={12}>
            <DashboardCard
              title="End Of Life"
              subtitle="EOL Advisories Across The Organization"
              icon={<WarningAmberIcon />}
              sx={{ height: 256 }}
            >
              <EndOfLife organizationMrn={org.mrn} />
            </DashboardCard>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}
